import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Mail, Phone, Heart, Sparkles, ShieldCheck, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSEO } from '../hooks/useSEO';

export const AboutPage = () => {
  useSEO({
    title: 'About CurveeForYou | Premium Shapewear from Los Angeles',
    description: "We're Mel and Mike — the husband-and-wife team behind CurveeForYou, a premium shapewear brand based in Los Angeles. Real people, real support, real confidence in every curve.",
    canonical: 'https://curveeforyou.com/about'
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/30 to-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="about-nav-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/feqbyn90_Logo%20curveeforyou.png"
                alt="CurveeForYou"
                className="h-20 md:h-24 w-auto"
              />
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-[#7d1f3d] text-[#7d1f3d] hover:bg-[#7d1f3d] hover:text-white" data-testid="about-back-button">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Shop
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <article className="container mx-auto px-6 py-12 max-w-3xl">
        {/* Hero */}
        <div className="text-center mb-14">
          <span className="inline-block bg-rose-100 text-[#7d1f3d] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
            About CurveeForYou
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-[#7d1f3d] leading-tight mb-6" data-testid="about-h1">
            We Started CurveeForYou Because Getting Dressed Should Feel Good.
          </h1>
          <p className="text-lg md:text-xl text-gray-700 italic leading-relaxed border-l-4 border-[#d4af37] pl-5 max-w-2xl mx-auto text-left">
            Not frustrating. Not complicated. Not a battle with your own reflection.
          </p>
        </div>

        {/* Intro */}
        <div className="space-y-5 text-base md:text-lg text-gray-800 leading-relaxed mb-14">
          <p>
            We&apos;ve all had that moment — the outfit you love, the event you&apos;ve been looking forward to, and then that feeling of not quite being ready underneath it all. <strong className="text-[#7d1f3d]">Mel and Mike</strong> built CurveeForYou to solve exactly that. Premium shapewear designed to smooth, snatch, and support real bodies, real curves, and real life.
          </p>
        </div>

        {/* Who We Are */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#7d1f3d] mb-5 flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-[#d4af37]" />
            Who We Are
          </h2>
          <div className="space-y-5 text-base md:text-lg text-gray-800 leading-relaxed">
            <p>
              CurveeForYou is a women&apos;s shapewear brand based in <strong>Los Angeles</strong>. Mel and Mike curate and stand behind every piece they carry — because what you wear underneath changes everything about how you carry yourself above it.
            </p>
            <p className="text-xl md:text-2xl text-[#7d1f3d] font-bold italic leading-snug py-4 px-6 bg-rose-50 rounded-xl border-l-4 border-[#d4af37]">
              Snatched waist. Lifted silhouette. Smooth from every angle. That&apos;s not a filter — that&apos;s the right foundation.
            </p>
            <p>
              We are not a faceless website. <strong>Mel answers your emails. Mike picks up the phone.</strong> And they both genuinely care whether your order makes you feel incredible.
            </p>
          </div>
        </section>

        {/* What We Believe */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#7d1f3d] mb-5 flex items-center gap-3">
            <Heart className="w-7 h-7 text-[#d4af37]" />
            What We Believe
          </h2>

          <div className="space-y-6 text-base md:text-lg text-gray-800 leading-relaxed">
            <div className="border-l-4 border-[#7d1f3d] pl-5">
              <h3 className="font-bold text-gray-900 mb-1">Confidence is not one size.</h3>
              <p>We curate for curves. Real ones. Because the women we serve are not an afterthought — they are the whole point.</p>
            </div>

            <div className="border-l-4 border-[#7d1f3d] pl-5">
              <h3 className="font-bold text-gray-900 mb-1">Luxury should be accessible.</h3>
              <p>Premium sculpting shapewear shouldn&apos;t cost a fortune. CurveeForYou brings high-quality, body-loving pieces to every woman — not just a select few.</p>
            </div>

            <div className="border-l-4 border-[#7d1f3d] pl-5">
              <h3 className="font-bold text-gray-900 mb-1">Price should never be the reason you say no.</h3>
              <p>We keep our prices real because we&apos;d rather have you in the right piece than watching it sit in your cart. Great shapewear shouldn&apos;t be a splurge — it should just be yours.</p>
            </div>

            <div className="border-l-4 border-[#7d1f3d] pl-5">
              <h3 className="font-bold text-gray-900 mb-1">You deserve real support.</h3>
              <p>Not just physically — though our shapewear does that beautifully. We mean someone in your corner, helping you find the right fit and the right snatch.</p>
            </div>

            <div className="border-l-4 border-[#7d1f3d] pl-5">
              <h3 className="font-bold text-gray-900 mb-1">If you&apos;re not happy, we&apos;re not happy.</h3>
              <p>Every purchase is backed by our 30-day guarantee. If something isn&apos;t right, Mel and Mike will make it right — no runaround, no fine print.</p>
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#7d1f3d] mb-5 flex items-center gap-3">
            <Truck className="w-7 h-7 text-[#d4af37]" />
            How We Work
          </h2>
          <div className="space-y-5 text-base md:text-lg text-gray-800 leading-relaxed">
            <p>
              Every order ships from <strong>Los Angeles</strong>. That means faster delivery, real accountability, and two real people you can actually reach when you need them.
            </p>
            <p>
              Want to walk into that room snatched? <strong>They&apos;ll help you find exactly what does it.</strong> Not sure about sizing? Mike will walk you through it. Something arrived and it&apos;s not what you expected? Mel will sort it out. They are here before the sale, during the sale, and long after the package arrives.
            </p>
          </div>
        </section>

        {/* Our Promise */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#7d1f3d] mb-5 flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-[#d4af37]" />
            Our Promise
          </h2>
          <div className="space-y-5 text-base md:text-lg text-gray-800 leading-relaxed">
            <p>
              Mel and Mike will never sell you something they wouldn&apos;t stand behind. Every piece on CurveeForYou is chosen for its quality, its compression, and its ability to make you feel exactly the way you deserve to feel when you walk into a room.
            </p>
            <p className="text-2xl md:text-3xl text-[#7d1f3d] font-bold italic leading-snug text-center py-6">
              Smooth. Snatched. Completely yourself.
            </p>
            <p className="text-center text-lg text-gray-700">
              Because when you feel this good inside, it shows.
            </p>
          </div>
        </section>

        {/* Tagline + Contact */}
        <section className="mt-16 mb-12 p-10 bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl shadow-2xl text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-2 text-[#d4af37]">CurveeForYou</h3>
          <p className="text-xl md:text-2xl font-semibold italic mb-8">Confidence in Every Curve.</p>

          <div className="space-y-3 text-rose-100 mb-8">
            <div className="flex items-center justify-center gap-2">
              <MapPin className="w-5 h-5 text-[#d4af37]" />
              <span>Los Angeles, California</span>
            </div>
            <a href="mailto:info@curveeforyou.com" className="flex items-center justify-center gap-2 hover:text-white transition-colors">
              <Mail className="w-5 h-5 text-[#d4af37]" />
              <span>info@curveeforyou.com</span>
            </a>
            <a href="tel:18002985173" className="flex items-center justify-center gap-2 hover:text-white transition-colors">
              <Phone className="w-5 h-5 text-[#d4af37]" />
              <span>1-800-298-5173</span>
            </a>
          </div>

          <Link to="/">
            <Button className="bg-[#d4af37] hover:bg-[#c49d2a] text-[#5d1729] font-bold px-8 py-6 text-lg" data-testid="about-shop-cta">
              Shop the Faja — $41.99
            </Button>
          </Link>
        </section>
      </article>
    </div>
  );
};
